import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldStar, Briefcase, ArrowsClockwise, MicrosoftOutlookLogo,
  CheckCircle, Clock, Prohibit, PencilSimple, X, CaretDown, MagnifyingGlass, Buildings,
} from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/utils";

const ROLE_LABELS = { admin: "Admin", manager: "Manager", user: "User" };
const ROLE_ORDER = { admin: 0, manager: 1, user: 2 };
const STATUS_STYLES = {
  active: { bg: "bg-emerald-50", text: "text-emerald-700", Icon: CheckCircle, label: "Active" },
  pending: { bg: "bg-amber-50", text: "text-amber-700", Icon: Clock, label: "Pending" },
  disabled: { bg: "bg-slate-100", text: "text-slate-500", Icon: Prohibit, label: "Disabled" },
};
const ROLE_ICONS = { admin: ShieldStar, manager: Buildings, user: Briefcase };

export default function UsersTab({ onChanged }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const departments = useMemo(() => {
    const set = new Set();
    users.forEach((u) => { if (u.department) set.add(u.department); });
    return Array.from(set).sort();
  }, [users]);

  const patchUser = async (id, patch, successMsg) => {
    try {
      await api.patch(`/users/${id}`, patch);
      if (successMsg) toast.success(successMsg);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Remove ${u.email}? They will lose all access.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User removed");
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const syncMicrosoft = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/users/sync-microsoft");
      toast.success(`Synced — ${data.inserted} new, ${data.updated} updated`);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Microsoft sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Filter and sort
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users;
    if (q) {
      list = list.filter((u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.department || "").toLowerCase().includes(q) ||
        (u.job_title || "").toLowerCase().includes(q));
    }
    if (roleFilter !== "all") list = list.filter((u) => u.role === roleFilter);
    return [...list].sort((a, b) => {
      const r = (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9);
      if (r !== 0) return r;
      return (a.name || a.email).localeCompare(b.name || b.email);
    });
  }, [users, search, roleFilter]);

  const pending = users.filter((u) => u.status === "pending");
  const counts = {
    admin: users.filter((u) => u.role === "admin").length,
    manager: users.filter((u) => u.role === "manager").length,
    user: users.filter((u) => u.role === "user").length,
  };

  return (
    <div data-testid="users-tab" className="space-y-8">
      {/* Header */}
      <section className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-sm bg-slate-900/5 flex items-center justify-center shrink-0">
            <ShieldCheck size={24} className="text-slate-900" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-2xl text-slate-900">Users &amp; Roles</h2>
            <p className="text-sm text-slate-500 mt-1 leading-relaxed">
              Only people on your Microsoft tenant can be added — sync the directory then assign each person a role.
              <span className="block mt-1 text-slate-400 text-xs">
                <span className="font-medium text-slate-600">Admin</span> sees everything ·{" "}
                <span className="font-medium text-slate-600">Manager</span> sees their own + their department's leads ·{" "}
                <span className="font-medium text-slate-600">User</span> sees only what they're assigned.
              </span>
            </p>
          </div>
          <Button
            data-testid="sync-users-btn"
            onClick={syncMicrosoft}
            disabled={syncing}
            className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2"
          >
            <MicrosoftOutlookLogo size={14} className="text-[#5fb4ff]" />
            {syncing ? "Syncing…" : "Sync from Microsoft"}
          </Button>
        </div>

        {/* Counts strip */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
          <CountBox label="Total" value={users.length} />
          <CountBox label="Admins" value={counts.admin} accent="bg-slate-900 text-white" />
          <CountBox label="Managers" value={counts.manager} accent="bg-indigo-50 text-indigo-700" />
          <CountBox label="Users" value={counts.user} accent="bg-slate-50 text-slate-700" />
        </div>
      </section>

      {pending.length > 0 && (
        <section data-testid="pending-users-section" className="bg-amber-50/40 border border-amber-200 rounded-sm">
          <div className="px-6 py-4 border-b border-amber-200 flex items-center gap-2">
            <Clock size={16} className="text-amber-700" weight="fill" />
            <div className="label-tracking text-amber-800">Pending approval ({pending.length})</div>
          </div>
          <ul>
            {pending.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                me={me}
                departments={departments}
                onPatch={patchUser}
                onDelete={deleteUser}
                highlight
              />
            ))}
          </ul>
        </section>
      )}

      {/* Filter bar */}
      <section className="bg-white border border-slate-200 rounded-sm">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="label-tracking">Directory ({filtered.length})</div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="user-search"
                placeholder="Search name, email, department…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 rounded-sm pl-8 w-[280px] text-sm"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="role-filter" className="h-8 rounded-sm w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="admin">Admins</SelectItem>
                <SelectItem value="manager">Managers</SelectItem>
                <SelectItem value="user">Users</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="p-6 text-sm text-slate-500" data-testid="users-loading">Loading users…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="users-empty">
            <Buildings size={36} className="text-slate-300 mx-auto" weight="duotone" />
            <p className="text-sm text-slate-500 mt-3">
              No users match. {users.length === 0 ? "Click Sync from Microsoft to import your tenant directory." : "Adjust your filters."}
            </p>
          </div>
        ) : (
          <ul>
            {filtered.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                me={me}
                departments={departments}
                onPatch={patchUser}
                onDelete={deleteUser}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CountBox({ label, value, accent = "bg-slate-50 text-slate-700" }) {
  return (
    <div className={`rounded-sm py-3 ${accent}`}>
      <div className="font-display text-2xl leading-none">{value}</div>
      <div className="label-tracking !text-[0.6rem] mt-1 opacity-70">{label}</div>
    </div>
  );
}

function UserRow({ u, me, departments, onPatch, onDelete, highlight = false }) {
  const isMe = (me?.email || "").toLowerCase() === u.email.toLowerCase();
  const styles = STATUS_STYLES[u.status] || STATUS_STYLES.active;
  const StatusIcon = styles.Icon;
  const RoleIcon = ROLE_ICONS[u.role] || Briefcase;
  const [editDept, setEditDept] = useState(false);
  const [deptDraft, setDeptDraft] = useState(u.department || "");
  const initials = (u.name || u.email)
    .split(/[ .@]/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");

  const saveDept = () => {
    setEditDept(false);
    if ((deptDraft || "") === (u.department || "")) return;
    onPatch(u.id, { department: deptDraft.trim() || null, department_override: true }, "Department updated");
  };

  return (
    <li
      data-testid={`user-row-${u.email}`}
      className={
        "px-6 py-4 flex items-center gap-4 flex-wrap border-b border-slate-100 last:border-b-0 " +
        (highlight ? "bg-amber-50/30" : "")
      }
    >
      <div className="h-9 w-9 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center shrink-0">
        {initials || "?"}
      </div>

      <div className="flex-1 min-w-0 max-w-md">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 truncate">{u.name || u.email.split("@")[0]}</span>
          {isMe && <span className="label-tracking !text-[0.55rem] text-slate-400">You</span>}
          <span className={`inline-flex items-center gap-1 text-[0.65rem] px-1.5 py-0.5 rounded-sm ${styles.bg} ${styles.text}`}>
            <StatusIcon size={11} weight="fill" /> {styles.label}
          </span>
          <span className="inline-flex items-center gap-1 text-[0.65rem] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700">
            <RoleIcon size={11} weight="fill" /> {ROLE_LABELS[u.role] || u.role}
          </span>
        </div>
        <div className="text-xs text-slate-500 truncate font-mono mt-0.5">{u.email}</div>
        {u.job_title && (
          <div className="text-[0.7rem] text-slate-500 mt-0.5 truncate">{u.job_title}</div>
        )}
        <div className="text-[0.65rem] text-slate-400 mt-1 flex items-center gap-2 flex-wrap">
          {editDept ? (
            <span className="inline-flex items-center gap-1">
              <Input
                autoFocus
                value={deptDraft}
                onChange={(e) => setDeptDraft(e.target.value)}
                onBlur={saveDept}
                onKeyDown={(e) => { if (e.key === "Enter") saveDept(); if (e.key === "Escape") { setEditDept(false); setDeptDraft(u.department || ""); }}}
                placeholder="Department"
                className="h-6 px-1.5 rounded-sm w-[160px] text-[0.7rem]"
                data-testid={`dept-input-${u.email}`}
              />
            </span>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-1 hover:text-slate-700 group"
              onClick={() => { setDeptDraft(u.department || ""); setEditDept(true); }}
              data-testid={`dept-edit-${u.email}`}
              title="Click to edit department"
            >
              <Buildings size={10} />
              <span>{u.department || "No department"}</span>
              {u.department_override && (
                <span className="text-amber-600 ml-0.5" title="Manual override (ignores MS sync)">·override</span>
              )}
              <PencilSimple size={10} className="opacity-0 group-hover:opacity-60" />
            </button>
          )}
          {u.last_login_at && <span>· Last seen {formatDate(u.last_login_at)}</span>}
          {u.last_synced_at && !u.last_login_at && <span>· Synced {formatDate(u.last_synced_at)}</span>}
        </div>

        {/* Manager-only: managed departments */}
        {u.role === "manager" && (
          <div className="mt-2">
            <ManagedDepartmentsPicker
              user={u}
              departments={departments}
              onSave={(list) => onPatch(u.id, { managed_departments: list }, "Manager scope updated")}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto flex-wrap">
        {u.status === "pending" && (
          <Button
            data-testid={`approve-${u.email}`}
            size="sm"
            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 h-8 text-xs gap-1"
            onClick={() => onPatch(u.id, { status: "active" }, "User approved")}
          >
            <CheckCircle size={12} weight="fill" /> Approve
          </Button>
        )}

        <label
          className="inline-flex items-center gap-1.5 text-[0.7rem] text-slate-600 cursor-pointer select-none px-2 h-8 rounded-sm border border-slate-200"
          title={u.role === "admin" ? "Admins always have Sales access regardless of this box" : "Tick to give this user access to the Sales tab"}
          data-testid={`incl-sales-label-${u.email}`}
        >
          <Checkbox
            checked={!!u.incl_sales}
            onCheckedChange={(v) => onPatch(u.id, { incl_sales: !!v }, v ? "Sales access granted" : "Sales access removed")}
            data-testid={`incl-sales-${u.email}`}
          />
          Incl Sales{u.role === "admin" && <span className="text-slate-400">(always)</span>}
        </label>

        <Select
          value={u.role}
          onValueChange={(v) => onPatch(u.id, { role: v }, `Set to ${ROLE_LABELS[v]}`)}
        >
          <SelectTrigger
            data-testid={`role-select-${u.email}`}
            className="rounded-sm h-8 w-[120px] text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="manager">Manager</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={u.status}
          onValueChange={(v) => onPatch(u.id, { status: v }, `Marked ${v}`)}
        >
          <SelectTrigger
            data-testid={`status-select-${u.email}`}
            className="rounded-sm h-8 w-[110px] text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>

        <Button
          data-testid={`delete-${u.email}`}
          size="sm"
          variant="outline"
          disabled={isMe}
          className="rounded-sm h-8 px-2 text-slate-500 hover:text-red-600"
          onClick={() => onDelete(u)}
          title={isMe ? "You cannot delete yourself" : "Remove user"}
        >
          <X size={14} />
        </Button>
      </div>
    </li>
  );
}

function ManagedDepartmentsPicker({ user, departments, onSave }) {
  const own = user.department;
  const selected = user.managed_departments || [];
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(selected);

  useEffect(() => { setDraft(selected); /* eslint-disable-next-line */ }, [open]);

  const allDepts = useMemo(() => {
    const s = new Set(departments);
    if (own) s.delete(own); // own is always implicit
    return Array.from(s).sort();
  }, [departments, own]);

  const toggle = (d) => {
    setDraft((cur) => cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]);
  };

  const commit = () => {
    setOpen(false);
    // Avoid no-op writes
    const same = draft.length === selected.length && draft.every((d) => selected.includes(d));
    if (!same) onSave(draft);
  };

  const label = selected.length === 0
    ? (own ? `Manages ${own} only` : "No departments — assign one")
    : `${own ? own + " + " : ""}${selected.length} more dept${selected.length === 1 ? "" : "s"}`;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) commit(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`managed-depts-${user.email}`}
          className="inline-flex items-center gap-1.5 text-[0.7rem] px-2 py-1 rounded-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
        >
          <Buildings size={11} weight="fill" />
          <span>{label}</span>
          <CaretDown size={10} />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 rounded-sm" align="start">
        <div className="p-3 border-b border-slate-100">
          <div className="label-tracking">Manages departments</div>
          <p className="text-[0.7rem] text-slate-500 mt-1">
            Always sees their own dept{own ? ` (${own})` : ""}. Tick any others they should also oversee.
          </p>
        </div>
        <div className="max-h-64 overflow-y-auto py-2">
          {allDepts.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">No other departments synced yet.</p>
          ) : allDepts.map((d) => (
            <label
              key={d}
              className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-xs"
            >
              <Checkbox
                checked={draft.includes(d)}
                onCheckedChange={() => toggle(d)}
                data-testid={`dept-check-${d}`}
              />
              <span className="truncate">{d}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 p-2 border-t border-slate-100">
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDraft(selected); setOpen(false); }}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs rounded-sm bg-slate-900 hover:bg-slate-800"
            onClick={commit}
            data-testid={`save-managed-depts-${user.email}`}
          >
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
